'use client';

import React from 'react';
import { useState, type ReactNode } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../primitives/alert-dialog';
import { buttonVariants } from '../../primitives/button';
import { cn } from '../../utils/cn';

/**
 * UzConfirmDialog - destructive confirmation modal.
 *
 * Use whenever an action is irreversible (delete, cancel booking, refund).
 * The action label defaults to the destructive variant; pass `tone='primary'`
 * for non-destructive confirms.
 */

export interface UzConfirmDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Trigger element. asChild semantics. */
  trigger?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Visual tone for the confirm button. */
  tone?: 'destructive' | 'primary';
  /** Called when user confirms. Can be async; UI awaits before closing. */
  onConfirm: () => void | Promise<void>;
}

export const UzConfirmDialog = ({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'destructive',
  onConfirm,
}: UzConfirmDialogProps): React.JSX.Element => {
  const [pending, setPending] = useState(false);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      {trigger && <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            className={cn(tone === 'destructive' && buttonVariants({ variant: 'destructive' }))}
            onClick={async (e) => {
              // Prevent default close so we can await async onConfirm
              if (onConfirm.constructor.name === 'AsyncFunction') {
                e.preventDefault();
                setPending(true);
                try {
                  await onConfirm();
                  onOpenChange?.(false);
                } finally {
                  setPending(false);
                }
              } else {
                onConfirm();
              }
            }}
          >
            {pending ? 'Working...' : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
